import { get } from "lodash"
import { CustomFilterState, DataTableFilterState } from "../components/Tables/DataTable/DataTable"
import { ColumnConfig } from "../types"
import {
  checkDigitsOnly,
  ensureArrayFormat,
  normaliseStringForComparison,
  toLowerCaseCustom,
  toUpperCaseCustom,
  toValidPfDate,
  toValidPfDateObject,
} from "../utils"

export class FilterService {
  shouldCalculateFilterState = (currentFilterState: DataTableFilterState) => {
    let recalculate = false
    Object.keys(currentFilterState).forEach((headerKey) => {
      if (!Object.keys(currentFilterState[headerKey]).length) {
        recalculate = true
      }
    })
    return recalculate
  }

  generateFilterState = (
    columnConfig: ColumnConfig[],
    tableData: Record<string, string>[],
    initialFilterState: DataTableFilterState,
  ) => {
    const tableHeaderKeys = columnConfig.map((item) => item.key)
    const allFilterState = {} as DataTableFilterState

    tableHeaderKeys.forEach((headerKey) => {
      if (!allFilterState[headerKey]) {
        allFilterState[headerKey] = {}
      }
      if (initialFilterState[headerKey]) {
        allFilterState[headerKey] = initialFilterState[headerKey]
      }
    })

    tableData.forEach((item) => {
      tableHeaderKeys.forEach((headerKey) => {
        try {
          const value = item[headerKey]
          let valueArray: string[] = []

          if (Array.isArray(value)) {
            valueArray = value.map(String)
          } else if (typeof value === "number" || typeof value === "string") {
            valueArray = [String(value)]
          }

          valueArray.forEach((value) => {
            value = toUpperCaseCustom(value).trim()
            if (value !== "" && value !== "-" && !(value in allFilterState[headerKey])) {
              allFilterState[headerKey][value] = false
            }
          })
        } catch (error) {
          console.error("error: ", error)
        }
      })
    })

    return allFilterState
  }

  applyFilters = (tableData: Record<string, any>[], allFilterState: DataTableFilterState) => {
    // TODO: Remove this as soon as legacy Table component has been fully replaced byu the simplygrow-ui equivalent.
    try {
      let hasFiltersApplied = false

      for (const headerKey in allFilterState) {
        const headerFilters = allFilterState[headerKey]
        for (const filterKey in headerFilters) {
          if (headerFilters[filterKey]) {
            hasFiltersApplied = true
            break
          }
        }
        if (hasFiltersApplied) break
      }

      if (!hasFiltersApplied) {
        return tableData
      }

      const activeFilters: Record<string, string> = {}
      for (const headerKey in allFilterState) {
        const headerFilters = allFilterState[headerKey]
        for (const filterKey in headerFilters) {
          if (headerFilters[filterKey]) {
            activeFilters[headerKey] = filterKey.trim().toUpperCase()
          }
        }
      }

      return tableData.filter((dataItem) => {
        return Object.keys(activeFilters).every((headerKey: string) => {
          const value = dataItem[headerKey]
          if (value !== undefined) {
            const valueArray: string[] = ensureArrayFormat(value)
            const matchesFilter = valueArray.some((item) => {
              const filterValue = normaliseStringForComparison(item)
              return activeFilters[headerKey] === filterValue
            })
            if (matchesFilter) {
              return true
            }
          }
          return false
        })
      })
    } catch (error) {
      throw error
    }
  }

  cleanFilterState = (customFilterState: CustomFilterState[]) => {
    return customFilterState.filter((item) => {
      if (!item.selectedField || item.filterValue === "") {
        return false
      }
      return true
    })
  }

  applyCustomFilters = (data: any[], customFilterState: CustomFilterState[], dataPath?: string[]) => {
    customFilterState.forEach((filterStateItem) => {
      const { filterType, filterValue, key } = filterStateItem
      const normalisedFilterValue = normaliseStringForComparison(filterValue)
      const filterValueIsNumber = checkDigitsOnly(normalisedFilterValue)
      const maybeDateFilterValue = toValidPfDateObject(normalisedFilterValue)
      data = [...data].filter((rowItem) => {
        let completeDataPath = key as string
        if (dataPath) {
          completeDataPath = [...dataPath, key].join(".")
        }
        const value = get(rowItem, completeDataPath, "")
        const arrayOfValues = ensureArrayFormat(value)
        let shouldReturn = false
        for (const arrayValue of arrayOfValues) {
          const normalisedArrayValue = normaliseStringForComparison(arrayValue)
          const maybeDateArrayValue = toValidPfDateObject(normalisedArrayValue)
          const arrayValueIsNumber = checkDigitsOnly(normalisedArrayValue)

          if (
            (filterType === "=" && normalisedArrayValue.includes(normalisedFilterValue)) ||
            (filterType === ">=" &&
              arrayValueIsNumber &&
              filterValueIsNumber &&
              parseInt(normalisedArrayValue) >= parseInt(normalisedFilterValue)) ||
            (filterType === "<=" &&
              arrayValueIsNumber &&
              filterValueIsNumber &&
              parseInt(normalisedArrayValue) <= parseInt(normalisedFilterValue)) ||
            (filterType === "=" &&
              maybeDateArrayValue &&
              maybeDateFilterValue &&
              maybeDateArrayValue.getTime() === maybeDateFilterValue.getTime()) ||
            (filterType === ">=" &&
              maybeDateArrayValue &&
              maybeDateFilterValue &&
              maybeDateArrayValue.getTime() >= maybeDateFilterValue.getTime()) ||
            (filterType === "<=" &&
              maybeDateArrayValue &&
              maybeDateFilterValue &&
              maybeDateArrayValue.getTime() <= maybeDateFilterValue.getTime())
          ) {
            shouldReturn = true
            break
          }
        }
        return shouldReturn
      })
    })
    return data
  }
}
